import {
  useCreateConcordReplayMutation,
  useGetConcordReplayJobQuery,
  useGetConcordReplayJobsQuery,
} from '@/services/concordReplay';
import type { ConcordReplayJobSummary } from '@/services/concordReplay';
import { isErrorWithData } from '@/services/helpers';
import { Badge, Box, Button, FormikTextInput, toast } from '@palmetto/palmetto-components';
import { Field, Form, Formik } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { object, string } from 'yup';

import FormSection from '../Forms/FormSection';

const POLL_INTERVAL_MS = 5000;

const STATUS_BADGE_VARIANT: Record<string, 'success' | 'danger' | 'info' | 'warning' | 'default'> = {
  completed: 'success',
  failed: 'danger',
  processing: 'info',
  pending: 'warning',
  cancelled: 'default',
};

interface ReplayFormData {
  startQueryDate: string;
  endQueryDate: string;
}

const initialValues: ReplayFormData = { startQueryDate: '', endQueryDate: '' };

const formSchema = object().shape({
  startQueryDate: string().required('Start date is required'),
  endQueryDate: string()
    .required('End date is required')
    // Dates are YYYY-MM-DD, so a string comparison matches chronological order.
    .test('end-on-or-after-start', 'End date must be on or after start date', function endOnOrAfterStart(value) {
      const { startQueryDate } = this.parent as { startQueryDate?: string };
      if (!value || !startQueryDate) {
        return true;
      }
      return startQueryDate <= value;
    }),
});

const isActive = (status: string) => status === 'pending' || status === 'processing';

interface JobRowProps {
  job: ConcordReplayJobSummary;
  onView: (jobId: string) => void;
  isSelected: boolean;
}

function JobRow({ job, onView, isSelected }: JobRowProps) {
  const handleView = useCallback(() => onView(job.jobId), [onView, job.jobId]);
  return (
    <tr style={{ height: '44px', background: isSelected ? '#f3f4f6' : undefined }}>
      <td>{job.requestedByEmail ?? '—'}</td>
      <td>{job.requestedAt ? new Date(job.requestedAt).toLocaleString() : '—'}</td>
      <td>{`${job.startQueryDate} → ${job.endQueryDate}`}</td>
      <td>
        <Badge variant={STATUS_BADGE_VARIANT[job.status] ?? 'default'} message={job.status} />
      </td>
      <td>{job.initialFailed ?? '—'}</td>
      <td>{job.duplicates ?? '—'}</td>
      <td>{job.succeeded ?? '—'}</td>
      <td>{job.remainingFailed ?? '—'}</td>
      <td>
        <Button size="sm" variant="tertiary" type="button" onClick={handleView}>
          View logs
        </Button>
      </td>
    </tr>
  );
}

export function ReplayFailedConcordLogs() {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [listPolling, setListPolling] = useState<number>(0);
  const [detailPolling, setDetailPolling] = useState<number>(0);

  const [createReplay] = useCreateConcordReplayMutation();
  const { data: jobsData, refetch } = useGetConcordReplayJobsQuery(undefined, { pollingInterval: listPolling });
  const { data: detailData } = useGetConcordReplayJobQuery(selectedJobId ?? '', {
    skip: !selectedJobId,
    pollingInterval: detailPolling,
  });

  useEffect(() => {
    const anyActive = (jobsData?.jobs ?? []).some((j) => isActive(j.status));
    setListPolling(anyActive ? POLL_INTERVAL_MS : 0);
  }, [jobsData]);

  useEffect(() => {
    setDetailPolling(detailData?.job && isActive(detailData.job.status) ? POLL_INTERVAL_MS : 0);
  }, [detailData]);

  const handleView = useCallback((jobId: string) => setSelectedJobId(jobId), []);

  const handleSubmit = async (
    values: ReplayFormData,
    { setSubmitting }: { setSubmitting: (v: boolean) => void },
  ) => {
    try {
      const res = await createReplay({
        startQueryDate: values.startQueryDate,
        endQueryDate: values.endQueryDate,
      }).unwrap();
      setSelectedJobId(res.jobId);
      toast.success('Replay started.');
      refetch();
    } catch (error: unknown) {
      if (isErrorWithData(error)) {
        toast.error(error.data.message || 'Failed to start replay');
      } else {
        toast.error('An unexpected error occurred');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const logs = detailData?.job.logs ?? [];
  const logRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  return (
    <Box childGap="lg">
      <Formik initialValues={initialValues} validationSchema={formSchema} onSubmit={handleSubmit}>
        {({ isSubmitting }) => (
          <Form>
            <FormSection title="Run Replay" description="Replays failed Concord logs in the chosen date range.">
              <Box childGap="md">
                <Field
                  name="startQueryDate"
                  id="startQueryDate"
                  type="date"
                  component={FormikTextInput}
                  label="Start date"
                  isRequired
                />
                <Field
                  name="endQueryDate"
                  id="endQueryDate"
                  type="date"
                  component={FormikTextInput}
                  label="End date"
                  isRequired
                />
                <Box display="flex" justifyContent="flex-end">
                  <Button type="submit" variant="primary" isLoading={isSubmitting} isDisabled={isSubmitting}>
                    Run replay
                  </Button>
                </Box>
              </Box>
            </FormSection>
          </Form>
        )}
      </Formik>

      {selectedJobId && (
        <Box childGap="sm" margin="xl 0 0 0">
          <Box fontWeight="bold">Logs {detailData?.job ? `(${detailData.job.status})` : ''}</Box>
          <pre
            ref={logRef}
            style={{
              background: '#0b1021',
              color: '#d6e2f0',
              borderRadius: '6px',
              maxHeight: '360px',
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '12px',
              whiteSpace: 'pre-wrap',
              padding: '16px',
              margin: 0,
            }}
          >
            {logs.length ? logs.join('\n') : 'No log output yet…'}
          </pre>
        </Box>
      )}

      <Box childGap="sm" margin="xl 0 0 0">
        <Box fontWeight="bold" fontSize="lg">
          Run History
        </Box>
        <Box overflow="auto">
          <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Concord replay job history">
            <thead>
              <tr>
                {[
                  'Requested By',
                  'Requested At',
                  'Date Range',
                  'Status',
                  'Initial Failed',
                  'Duplicates',
                  'Succeeded',
                  'Remaining',
                  'Logs',
                ].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {jobsData?.jobs.map((job) => (
                <JobRow key={job.jobId} job={job} onView={handleView} isSelected={job.jobId === selectedJobId} />
              ))}
            </tbody>
          </table>
        </Box>
      </Box>
    </Box>
  );
}
